import React from "react";
import { SectionCard } from "./Ui";
import { Button } from "./ui/button";
import DataTableColumnHeader, { DataTableHeaderCell } from "./DataTableColumnHeader";
import DataTablePagination from "./DataTablePagination";

/**
 * Reusable list table: SectionCard + column sort/filter headers + body + optional pagination.
 *
 * @param {object} props
 * @param {import('../lib/dataTable').DataTableColumn[]} props.columns
 * @param {ReturnType<import('../hooks/useDataTable').useDataTable>} props.table
 * @param {object[]} [props.rows] - defaults to table.rows
 * @param {(row: object, index: number) => React.ReactNode} [props.renderRow] - return <tr>…</tr>
 * @param {boolean} [props.showBody=true]
 * @param {number} [props.totalCount] - server-side total (for empty state)
 */
export default function DataTableBlock({
  title,
  titleExtra = null,
  columns,
  table,
  rows: rowsProp,
  rowKey,
  renderRow,
  renderTrailingHeader = null,
  renderTrailingCell,
  loading = false,
  totalCount,
  emptyMessage = "No records yet.",
  emptyFilteredMessage = "No records match your filters.",
  onClearFilters,
  pagination = null,
  hint = "Click a column heading to sort or filter.",
  showHint = true,
  showHeader = true,
  showBody = true,
  wrapInSectionCard = true,
  sectionAction = null,
  exportButton = null,
  testId = "data-table",
  minWidth = "640px",
  className = "",
}) {
  const rows = rowsProp ?? table.rows;
  const hasFilters = table.hasActiveFilters;
  const serverSide = totalCount != null;
  const empty = !loading && (serverSide ? totalCount === 0 : rows.length === 0);

  const handleClear = () => {
    table.clearAllFilters();
    table.clearColumnSort();
    onClearFilters?.();
  };

  const filterOnlyColumns = columns.filter((c) => c.hideInTable);
  const visibleColumns = columns.filter((c) => !c.hideInTable);
  const trailing = Boolean(renderTrailingHeader || renderTrailingCell);
  const colSpan = visibleColumns.length + (trailing ? 1 : 0);

  const titleNode = (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span>
        {title}
        {loading ? " · Loading…" : ""}
      </span>
      {titleExtra}
      {hasFilters && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-xs h-7 px-2 text-muted-foreground"
          onClick={handleClear}
          data-testid={`${testId}-clear-filters`}
        >
          Clear filters
        </Button>
      )}
    </span>
  );

  const body = (
    <div className={className}>
      {showHint && (
        <p className="text-xs text-muted-foreground mb-3">
          {hint}
          {filterOnlyColumns.length > 0 ? " Extra filters are in the strip above the table." : ""}
        </p>
      )}
      {filterOnlyColumns.length > 0 && (
        <div className="overflow-x-auto mb-3 rounded-md border border-dashed border-border">
          <table className="w-full text-sm" data-testid={`${testId}-extra-filters`}>
            <thead>
              <tr className="text-left text-muted-foreground bg-muted/20">
                {filterOnlyColumns.map((col) => (
                  <DataTableColumnHeader key={col.id} column={col} table={table} />
                ))}
              </tr>
            </thead>
          </table>
        </div>
      )}
      <div className="overflow-x-auto rounded-lg border border-border/70">
        <table className="w-full text-sm" style={{ minWidth }} data-testid={testId}>
          {showHeader && (
            <thead>
              <tr className="table-head-row">
                {visibleColumns.map((col) => (
                  <DataTableColumnHeader key={col.id} column={col} table={table} />
                ))}
                {trailing && (
                  <DataTableHeaderCell>{renderTrailingHeader}</DataTableHeaderCell>
                )}
              </tr>
            </thead>
          )}
          {showBody && (
            <tbody>
              {rows.map((row, index) => {
                const key = rowKey ? rowKey(row, index) : row.id ?? index;
                if (renderRow) {
                  return <React.Fragment key={key}>{renderRow(row, index)}</React.Fragment>;
                }
                return (
                  <tr key={key} className="table-body-row">
                    {visibleColumns.map((col) => (
                      <td key={col.id} className={`px-3 py-2.5 text-foreground ${col.cellClassName || ""}`}>
                        {col.render ? col.render(row) : formatCell(row, col)}
                      </td>
                    ))}
                    {renderTrailingCell && (
                      <td className="px-3 py-2.5 text-foreground">{renderTrailingCell(row)}</td>
                    )}
                  </tr>
                );
              })}
              {empty && (
                <tr>
                  <td colSpan={colSpan} className="text-center py-12 text-sm text-muted-foreground">
                    {hasFilters ? emptyFilteredMessage : emptyMessage}
                  </td>
                </tr>
              )}
            </tbody>
          )}
        </table>
      </div>
      {pagination && <DataTablePagination {...pagination} testId={`${testId}-pagination`} />}
    </div>
  );

  if (!wrapInSectionCard) {
    const toolbar = exportButton || sectionAction ? (
      <div className="flex flex-wrap items-center justify-end gap-2 mb-3">
        {exportButton}
        {sectionAction}
      </div>
    ) : null;
    return (
      <div className={className} data-testid={testId}>
        {(title || hasFilters) ? <div className="text-sm font-medium mb-3">{titleNode}</div> : null}
        {toolbar}
        {body}
      </div>
    );
  }

  const cardAction = exportButton ? (
    <span className="flex items-center gap-2">
      {exportButton}
      {sectionAction}
    </span>
  ) : sectionAction;

  return (
    <SectionCard title={titleNode} action={cardAction} testId={`${testId}-section`}>
      {body}
    </SectionCard>
  );
}

function formatCell(row, column) {
  if (column.accessor) return column.accessor(row);
  if (column.accessorKey) {
    const v = row[column.accessorKey];
    return v == null || v === "" ? "—" : v;
  }
  return row[column.id] ?? "—";
}

/** Build a standard list title with optional filter suffix. */
export function dataTableListTitle(label, count, { filtered, hasFilters } = {}) {
  if (hasFilters && filtered != null && filtered !== count) {
    return `${filtered} of ${count} ${label}`;
  }
  return `${count} ${label}`;
}
